import { z } from 'zod'
import { DeliveryMode, LocaleCode } from './enums.js'

/**
 * Manual activation, the share package and the explicit reveal — SM4 (B10.1, B7.5; MCP-AC-044..046).
 *
 * Three facts shape this file, and each is a literal or a derived value rather than a sentence:
 *
 * 1. **SignBento never sends a manual share.** The host's own Gmail/Slack transmission is *manual*,
 *    so the transport is the literal `'MANUAL'`, `delivery_credit` is the literal `0`,
 *    `transport_delivery_recorded` and `link_open_recorded` are the literal `false` — there is no
 *    field by which a caller could claim a delivery, a receipt or an open. The service records the
 *    share-package issuance and the sender's intent (`manual_share_intent`), and nothing else
 *    (§9.5, MET-15, OP-021, V17-AC-011).
 * 2. **The share-link classification is derived, never assumed.** `share_link_kind` is
 *    `MANUAL_CAPABILITY_LINK` exactly when the envelope's stored `delivery_mode` is `MANUAL`; a
 *    managed envelope has no manual link to hand out and is refused instead. It is never read from
 *    a request field, because a request field is not entitlement state.
 * 3. **The link is the recipient's capability link, composed exactly as the managed lane composes
 *    it** (`/sign/<capability token>#k=<envelope key>`): the token resolves through the API's own
 *    capability lookup and the fragment key opens the sealed package. It is returned only by the
 *    two explicit operations here — no read tool has a field for it (`capability_links` is the
 *    literal `null` in the commit and envelope contracts, MCP-AC-044).
 *
 * `P17-10` (which field types may appear in AI-host content) stays open: nothing here carries a
 * field value, a document byte or an instruction — the plaintext **consent text** is §7.4's public
 * recipient-facing copy, not agreement content.
 */

const Id = z.string().min(1).max(128)
const IsoDateTime = z.string().datetime()
const Sha256Hex = z.string().regex(/^[0-9a-f]{64}$/)

/* ------------------------------- the package ------------------------------- */

/** How a share package reaches a recipient. SignBento's manual lane is the only member. */
export const MCP_SHARE_TRANSPORTS = ['MANUAL'] as const
export const McpShareTransport = z.enum(MCP_SHARE_TRANSPORTS)
export type McpShareTransport = z.infer<typeof McpShareTransport>

/**
 * What the link *is*, derived from the envelope's stored delivery mode — never from a request.
 * `MANUAL_CAPABILITY_LINK` is a link the sender shares themselves; `MANAGED_INVITATION` is a link
 * the service dispatches, which this lane never issues.
 */
export const MCP_SHARE_LINK_KINDS = ['MANUAL_CAPABILITY_LINK', 'MANAGED_INVITATION'] as const
export const McpShareLinkKind = z.enum(MCP_SHARE_LINK_KINDS)
export type McpShareLinkKind = z.infer<typeof McpShareLinkKind>

export function shareLinkKindOf(deliveryMode: string): McpShareLinkKind {
  return deliveryMode === 'MANUAL' ? 'MANUAL_CAPABILITY_LINK' : 'MANAGED_INVITATION'
}

/**
 * The plaintext consent the recipient is shown before signing (§7.4, AUD-01). It travels so the
 * host can tell the person exactly what the signer will be asked to accept; `text_hash` is the
 * digest the accepted response will bind, so the host cannot paraphrase it into something else.
 */
export const McpShareConsent = z
  .object({
    version: z.string().min(1).max(40),
    locale: LocaleCode,
    text: z.string().min(1).max(2000),
    text_hash: Sha256Hex,
  })
  .strict()
export type McpShareConsent = z.infer<typeof McpShareConsent>

/**
 * The sender's own step (D02). A sender who signs is not a bearer of a capability link: their step
 * is an *adoption* of the frozen payload, so this reports whether it was adopted rather than
 * pretending a link exists for them.
 */
export const McpShareAdoption = z
  .object({ adopted: z.boolean(), adopted_at: IsoDateTime.nullable() })
  .strict()
export type McpShareAdoption = z.infer<typeof McpShareAdoption>

/**
 * One signer's share package: the short factual message, the signer's name and role, the deadline
 * and the authorized link. Every field is server-derived; the message is
 * `manualShareMessage`'s, so a host cannot compose a promise SignBento has not made.
 */
export const McpSignerSharePackage = z
  .object({
    participant_id: Id,
    role_id: z.string().min(1).max(64).nullable(),
    signer_name: z.string().min(1).max(200),
    /** The participant's role, or `signer` when the envelope recorded none. */
    signer_role: z.string().min(1).max(64),
    order_index: z.number().int().nonnegative(),
    short_message: z.string().min(1).max(600),
    document_title: z.string().min(1).max(300),
    expiration: IsoDateTime,
    capability_link: z.string().min(1).max(2048),
    capability_version: z.number().int().positive(),
    share_link_kind: McpShareLinkKind,
    share_transport: McpShareTransport,
    /** Zero: sharing it yourself is not a delivery SignBento performed or charged for. */
    delivery_credit: z.literal(0),
    consent: McpShareConsent,
  })
  .strict()
export type McpSignerSharePackage = z.infer<typeof McpSignerSharePackage>

/* --------------------------------- requests -------------------------------- */

/**
 * The manual-share commit. `review_id` is the whole input beyond the request id: there is no field
 * for an envelope, a participant, a link or a document, so "share something other than the action a
 * person approved" is unrepresentable rather than refused.
 */
export const McpManualShareCommitRequest = z
  .object({ client_request_id: z.string().min(1).max(128), review_id: Id })
  .strict()
export type McpManualShareCommitRequest = z.infer<typeof McpManualShareCommitRequest>

/**
 * The explicit reveal. It names exactly one participant of one agreement — never "all of them",
 * never "the next one" — so a link is returned only for the participant that was asked for.
 */
export const McpSigningLinkRequest = z.object({ envelope_id: Id, participant_id: Id }).strict()
export type McpSigningLinkRequest = z.infer<typeof McpSigningLinkRequest>

/* --------------------------------- results --------------------------------- */

/**
 * The manual-share commit's answer. `share_package_issued` is true only when packages are present,
 * and the two `*_recorded` literals state in the payload itself that no transport delivery and no
 * link open was recorded — so a reader cannot mistake the record for a receipt.
 */
export const McpManualShareResult = z
  .object({
    review_id: Id,
    state: z.literal('SHARED'),
    envelope_id: Id,
    delivery_mode: DeliveryMode,
    share_link_kind: McpShareLinkKind,
    share_transport: McpShareTransport,
    delivery_credit: z.literal(0),
    credit_effect: z
      .object({
        kind: z.literal('none'),
        debited: z.literal(0),
        available_after: z.number().int().nonnegative(),
      })
      .strict(),
    share_package_issued: z.literal(true),
    transport_delivery_recorded: z.literal(false),
    link_open_recorded: z.literal(false),
    /** The packages for the participants that hold an authorized link right now, in frozen order. */
    packages: z.array(McpSignerSharePackage).min(1),
    /** The step the sender must hand the link to next, when the chain names one. */
    next_link_ready: z
      .object({
        participant_id: Id,
        order_index: z.number().int().nonnegative(),
      })
      .strict()
      .nullable(),
    issued_at: IsoDateTime,
  })
  .strict()
export type McpManualShareResult = z.infer<typeof McpManualShareResult>

/**
 * The explicit reveal's answer for one participant. It carries the same literals as the package, so
 * a reveal can never be read as a send, and it names the participant it is for.
 */
export const McpSigningLinkResult = z
  .object({
    envelope_id: Id,
    participant_id: Id,
    role_id: z.string().min(1).max(64).nullable(),
    signer_name: z.string().min(1).max(200),
    signer_role: z.string().min(1).max(64),
    order_index: z.number().int().nonnegative(),
    document_title: z.string().min(1).max(300),
    expiration: IsoDateTime,
    capability_link: z.string().min(1).max(2048),
    capability_version: z.number().int().positive(),
    short_message: z.string().min(1).max(600),
    share_link_kind: McpShareLinkKind,
    share_transport: McpShareTransport,
    delivery_credit: z.literal(0),
    transport_delivery_recorded: z.literal(false),
    link_open_recorded: z.literal(false),
    /** The plaintext consent the signer will be shown, and the digest their response will bind. */
    consent: McpShareConsent,
    /** The sender's own step, adopted rather than signed through a link (D02). */
    adoption: McpShareAdoption,
    revealed_at: IsoDateTime,
  })
  .strict()
export type McpSigningLinkResult = z.infer<typeof McpSigningLinkResult>

/* ----------------------------------- copy ---------------------------------- */

/**
 * The prewritten message, and the only one this lane can produce. It is factual: it names the
 * signer, the document and the deadline, and it **asks the person to send it** — it never says
 * SignBento sent it, never says the signer received it, and never promises delivery (ADD-049).
 */
export function manualShareMessage(input: {
  readonly signer_name: string
  readonly document_title: string
  readonly expiration: string
}): string {
  const until = input.expiration.slice(0, 10)
  return `Hi ${input.signer_name}, please review and sign "${input.document_title}" — the link works until ${until}.`
}
